import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Captcha } from './ui/Captcha';
import { api } from '../lib/api';
interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyName?: string;
}
export function BookingModal({
  isOpen,
  onClose,
  propertyName = 'Oshara Collective'
}: BookingModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [errors, setErrors] = useState({
    name: '',
    phone: '',
    captcha: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  if (!isOpen) return null;
  const validateForm = () => {
    const newErrors = {
      name: '',
      phone: '',
      captcha: ''
    };
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/[\s-]/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    if (!captchaToken) {
      newErrors.captcha = 'Please verify you are not a robot';
    }
    setErrors(newErrors);
    return !newErrors.name && !newErrors.phone && !newErrors.captcha;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    try {
      // Send to Formspree
      const response = await fetch('https://formspree.io/f/xanyqkwb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email || 'Not provided',
          property: propertyName,
          _subject: `New Enquiry for ${propertyName}`
        })
      });
      const responseData = await response.json();
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: ''
        });
        setCaptchaToken(null);
        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        console.error('Formspree error:', responseData);
        setSubmitStatus('error');
        setErrorMessage(responseData.error || responseData.errors?.map((e: any) => e.message).join(', ') || 'Failed to send enquiry. Please try again or contact us directly.');
      }
    } catch (error) {
      console.error('Enquiry submission error:', error);
      setSubmitStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const handleCaptchaVerify = (token: string) => {
    setCaptchaToken(token);
    setErrors(prev => ({
      ...prev,
      captcha: ''
    }));
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-13">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl animate-in fade-in zoom-in duration-200" data-id="element-14">
        {/* Close Button */}
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close modal" data-id="element-15">
          <X className="h-5 w-5 text-gray-500" data-id="element-16" />
        </button>

        {/* Header */}
        <div className="p-6 border-b border-gray-100" data-id="element-17">
          <h2 className="font-serif text-2xl font-bold text-[#2C2C2C] mb-1" data-id="element-18">
            Enquire About Your Stay
          </h2>
          <p className="text-sm text-[#5C5C5C]" data-id="element-19">{propertyName}</p>
        </div>

        {/* Success Message */}
        {submitStatus === 'success' && <div className="p-6 bg-green-50 border-b border-green-100" data-id="element-20">
            <p className="text-green-800 text-center font-medium" data-id="element-21">
              ✓ Enquiry sent successfully! We'll contact you soon.
            </p>
          </div>}

        {/* Error Message */}
        {submitStatus === 'error' && <div className="p-6 bg-red-50 border-b border-red-100" data-id="element-22">
            <p className="text-red-800 text-center font-medium" data-id="element-23">
              ✗ {errorMessage || 'Something went wrong. Please try again.'}
            </p>
          </div>}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-24">
          <Input label="Full Name *" name="name" type="text" placeholder="Enter your full name" value={formData.name} onChange={handleChange} error={errors.name} required data-id="element-25" />

          <Input label="Phone Number *" name="phone" type="tel" placeholder="Enter your phone number" value={formData.phone} onChange={handleChange} error={errors.phone} required data-id="element-26" />

          <Input label="Email (Optional)" name="email" type="email" placeholder="Enter your email address" value={formData.email} onChange={handleChange} data-id="element-27" />

          <div className="space-y-1" data-id="element-28">
            <Captcha onVerify={handleCaptchaVerify} data-id="element-29" />
            {errors.captcha && <p className="text-sm text-red-500" data-id="element-30">{errors.captcha}</p>}
          </div>

          <div className="pt-2 space-y-3" data-id="element-31">
            <Button type="submit" className="w-full" size="lg" isLoading={isSubmitting} disabled={isSubmitting || submitStatus === 'success'} data-id="element-32">
              {isSubmitting ? 'Sending...' : submitStatus === 'success' ? 'Sent!' : 'Send Enquiry'}
            </Button>
            <p className="text-xs text-center text-[#5C5C5C]" data-id="element-33">
              We'll contact you within 24 hours to discuss your stay
            </p>
          </div>
        </form>
      </div>
    </div>;
}